import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { PaymentSettings as PaymentSettingsType } from '../../../types/storeSettings';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';

// استيراد المكونات الفرعية
import PaymentMethodsSection from './payment/PaymentMethodsSection';
import BankAccountsSection from './payment/BankAccountsSection';
import PaymentGatewaysSection from './payment/PaymentGatewaysSection';

interface PaymentSettingsProps {
  settings: PaymentSettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

const PaymentSettings: React.FC<PaymentSettingsProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const { updateSection } = useStoreSettings();
  const [formData, setFormData] = useState<PaymentSettingsType>(settings);
  const [editing, setEditing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      console.log('Saving payment settings:', formData);
      onSave(true); // تفعيل حالة التحميل
      onError(null); // إزالة أي رسائل خطأ سابقة

      // حفظ البيانات
      await updateSection('payment', formData);

      // تعيين حالة النجاح
      onSuccess(true);

      // إغلاق وضع التحرير بعد الحفظ بنجاح
      setEditing(false);
    } catch (error) {
      console.error('Error saving payment settings:', error);
      onError('حدث خطأ أثناء حفظ إعدادات الدفع');
    } finally {
      onSave(false); // إيقاف حالة التحميل
    }
  };

  const handleCancel = () => {
    setFormData(settings);
    setEditing(false);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {/* رأس الصفحة مع زر التحرير */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            إعدادات الدفع
          </Typography>

          {!editing && (
            <Button
              startIcon={<EditIcon />}
              onClick={() => setEditing(true)}
              variant="outlined"
            >
              تعديل
            </Button>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary">
          قم بإدارة طرق الدفع المتاحة في متجرك، بما في ذلك الدفع عند الاستلام والتحويل البنكي وبوابات الدفع الإلكتروني.
        </Typography>

        {editing && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 243, 224, 0.5)', borderRadius: 1, border: '1px dashed', borderColor: 'warning.main' }}>
            <Typography variant="body2" color="warning.dark">
              <strong>وضع التحرير نشط:</strong> قم بتعديل الإعدادات ثم انقر على زر "حفظ التغييرات" في أسفل الصفحة عند الانتهاء.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* قسم طرق الدفع */}
      <PaymentMethodsSection
        formData={formData}
        setFormData={setFormData}
        editing={editing}
      />

      <Divider sx={{ my: 3 }} />

      {/* قسم الحسابات البنكية */}
      <BankAccountsSection
        formData={formData}
        setFormData={setFormData}
        editing={editing}
      />

      <Divider sx={{ my: 3 }} />

      {/* قسم بوابات الدفع الإلكتروني */}
      <PaymentGatewaysSection
        formData={formData}
        setFormData={setFormData}
        editing={editing}
      />

      {/* أزرار الحفظ والإلغاء في وضع التحرير */}
      {editing && (
        <Paper sx={{ mt: 4, p: 3, borderTop: '4px solid', borderColor: 'primary.main' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight="medium">
              هل انتهيت من التعديل؟
            </Typography>

            <Box sx={{ display: 'flex' }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleCancel}
                sx={{ mr: 2 }}
                size="large"
              >
                إلغاء التغييرات
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                size="large"
              >
                حفظ التغييرات
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default PaymentSettings;
